import { usePermissions, useTranslate } from 'react-admin';
import CustomList from '@/components/global/list/CustomList.jsx';
import { ReferenceArrayFilter, PeriodFilter } from '@/components/global/filters/filters.jsx';
import { useMemo } from 'react';
import { TransactionSummaryAnalytics } from '@/components/analytics/TransactionSummaryAnalytics.jsx';
import { TransactionInvalidContainersAnalytics } from '@/components/analytics/TransactionInvalidContainersAnalytics.jsx';
import { TransactionPayoutAnalytics } from '@/components/analytics/TransactionPayoutAnalytics.jsx';
import { TransactionMaterialAnalytics } from '@/components/analytics/TransactionMaterialAnalytics.jsx';
import { TransactionCountAnalytics } from '@/components/analytics/TransactionCountAnalytics.jsx';
import { TransactionPackageAnalytics } from '@/components/analytics/TransactionPackageAnalytics.jsx';
import { CollectedBagsAnalytics } from '@/components/analytics/CollectedBagsAnalytics.jsx';
import { TransactionCollectionPointRanking } from '@/components/analytics/TransactionCollectionPointRanking.jsx';
import { AnalyticsListActions } from '@/components/analytics/common/AnalyticsListActions';
import { SubContent } from '@/components/analytics/common/SubContent.jsx';
import CustomSelectArrayInput from '@/components/global/inputs/CustomSelectArrayInput.jsx';
import { provinceChoices } from '@/components/global/options/selectOptions';
import { useTheme, useMediaQuery } from '@mui/material';

const DashboardContent = () => {
    return (
        <>
            <TransactionSummaryAnalytics />
            <TransactionCountAnalytics />
            <TransactionPackageAnalytics />
            <TransactionMaterialAnalytics />
            <CollectedBagsAnalytics />
            <SubContent first={<TransactionInvalidContainersAnalytics />} second={<TransactionPayoutAnalytics />} />
            <SubContent first={<TransactionCollectionPointRanking />} />
        </>
    );
};

export const Dashboard = () => {
    const { permissions } = usePermissions();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const translate = useTranslate();
    const baseFilters = useMemo(
        () => [
            <PeriodFilter key='period' source='period' alwaysOn={true} />,
            <ReferenceArrayFilter
                key='collectionPointId_in'
                source='collectionPointId_in'
                label={translate('common.collectionPoint')}
                optionText={choice => choice.customId}
                reference='collection-point'
                searchFields={['customId']}
                displayDeleteIcon={true}
                forceDesktopOnMobile={true}
                alwaysOn={isMobile}
            />,
            <ReferenceArrayFilter
                key='city'
                source='collectionPoint->address->city_in'
                reference='city'
                label={translate('resources.campaign.labels.cities')}
                forceDesktopOnMobile={true}
                alwaysOn={isMobile}
            />,
            <CustomSelectArrayInput
                key='province'
                source='collectionPoint->address->province_in'
                choices={provinceChoices(translate)}
                label={translate('common.province')}
                forceDesktopOnMobile={true}
                alwaysOn={isMobile}
            />,
        ],
        [isMobile, translate],
    );

    const adminFilters = useMemo(
        () => [
            ...baseFilters,
            <ReferenceArrayFilter
                key='groupId_in'
                source='groupId_in'
                reference='device-group'
                label={translate('resources.device.fields.group')}
                displayDeleteIcon={true}
                forceDesktopOnMobile={true}
                alwaysOn={isMobile}
            />,
            <ReferenceArrayFilter
                key='contractorId_in'
                source='contractorId_in'
                reference='contractor'
                label={translate('common.contractor')}
                displayDeleteIcon={true}
                forceDesktopOnMobile={true}
                alwaysOn={isMobile}
            />,
        ],
        [baseFilters, isMobile, translate],
    );

    const filters = permissions?.isAdmin || permissions?.isBok ? adminFilters : baseFilters;

    return (
        <CustomList
            title={translate('menu.transactionAnalytics')}
            filters={filters}
            filterDefaultValues={{ period: '30' }}
            component='div'
            disablePagination={true}
            actions={<AnalyticsListActions />}
        >
            <DashboardContent />
        </CustomList>
    );
};
